import { FIELD_TYPES } from '../constants/fieldTypes';
import {
  asyncDebounce,
  getObjectToNumberArray,
  isArrayEmpty,
  isObjectEmpty,
  trimString,
} from './helpers';
import {
  forbiddenCharacters,
  isEmpty,
  validateFieldsInList,
  validateLength,
  validateRange,
} from './validation';
import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DuplicateIcon } from '../assets/icons/content_copy.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { ReactComponent as MoveIcon } from '../assets/icons/undo.svg';
import { ReactComponent as EditSalariesIcon } from '../assets/icons/elevator.svg';
import { API_UNIQUE_FRAMEWORK } from '../constants/apiRoutes';
import http from './http';
import { CURRENCIES } from '../constants/leveling';
import { SALARY_FIELDS } from '../components/pages/levelingPage/config';

const prepareLevelingLevelsTableData = (
  levels,
  renderDescription,
  renderAttributes,
  renderRange,
  renderPeople,
  renderActions,
  track,
  framework
) => {
  return levels.map(level => {
    return {
      id: level.id,
      name: level.name,
      description: renderDescription(level),
      attributes: renderAttributes(level.attributes),
      range: renderRange(level.attributes),
      minSalary: level.min_salary,
      maxSalary: level.max_salary,
      people: renderPeople(level, track, framework),
      actions: renderActions(level, track, framework),
    };
  });
};

const prepareLevelingTracksTableData = (
  tracks,
  renderDescription,
  renderAttributes,
  renderRange,
  renderPeople,
  renderActions,
  framework
) => {
  return tracks.map(track => ({
    ...track,
    levels: !isArrayEmpty(track.levels)
      ? prepareLevelingLevelsTableData(
          track.levels,
          renderDescription,
          renderAttributes,
          renderRange,
          renderPeople,
          renderActions,
          track,
          framework
        )
      : [],
  }));
};

export const prepareLevelingFrameworksData = (
  frameworks,
  renderDescription,
  renderAttributes,
  renderRange,
  renderPeople,
  renderActions = () => {}
) => {
  return frameworks?.map(framework => {
    const currency = isEmpty(framework.currency)
      ? CURRENCIES.find(curr => curr.key === framework.currency)?.value
      : '';

    return {
      ...framework,
      currency: currency || '',
      tracks: !isArrayEmpty(framework.tracks)
        ? prepareLevelingTracksTableData(
            framework.tracks,
            renderDescription,
            renderAttributes,
            renderRange,
            renderPeople,
            renderActions,
            framework
          )
        : [],
    };
  });
};

export const prepareDotsMenuItems = (
  translations,
  record,
  recordParent,
  canEdit,
  canDelete,
  onDelete,
  onEdit,
  onDuplicate,
  onMove,
  hasMoveOption = false,
  frameworkParent = {},
  onEditSalaries = () => {}
) => [
  {
    type: 'edit',
    label: translations.edit,
    shouldItemRender: () => record.name && canEdit,
    action: () => onEdit(record, recordParent, frameworkParent),
    icon: EditIcon,
  },
  ...(record.framework && !isArrayEmpty(record?.levels)
    ? [
        {
          type: 'editSalaries',
          label: translations.editSalaries,
          shouldItemRender: () => canEdit && isEmpty(recordParent.currency),
          action: () => onEditSalaries(record, recordParent, frameworkParent),
          icon: EditSalariesIcon,
        },
      ]
    : []),
  {
    type: 'move',
    label: translations.move,
    shouldItemRender: () => canEdit && hasMoveOption,
    action: () => onMove(recordParent, record, frameworkParent),
    icon: MoveIcon,
  },
  {
    type: 'duplicate',
    label: translations.duplicate,
    shouldItemRender: () => record.name && canEdit,
    action: () => onDuplicate(recordParent, record, frameworkParent),
    icon: DuplicateIcon,
  },
  {
    type: 'delete',
    label: translations.delete,
    shouldItemRender: () => record.name && canDelete,
    action: () => onDelete(record),
    icon: DeleteIcon,
  },
];

export const prepareInitialData = (
  data,
  isEdit,
  isFramework = false,
  isLevel = false,
  currencies = []
) => {
  const { RANGE_FROM, RANGE_TO } = FIELD_TYPES;
  const currency =
    isFramework && isEdit
      ? currencies.find(curr => curr.value === data.currency)
      : '';

  if (isEdit) {
    return {
      id: data.id,
      name: data.name,
      ...(isFramework
        ? {
            hasSalaryInfo: isEmpty(currency),
            currency: currency || currencies?.[0],
          }
        : {}),
      ...(isLevel
        ? {
            attributes: data.attributes?.map(item => {
              return {
                ...item.attribute,
                [RANGE_FROM]: item[RANGE_FROM],
                [RANGE_TO]: item[RANGE_TO],
              };
            }),
          }
        : {}),
      ...(isLevel ? { description: data.description } : {}),
      ...(isLevel ? { users: data.users } : {}),
      ...(isLevel
        ? {
            salaryRange: [
              {
                min_salary: data.min_salary,
                max_salary: data.max_salary,
              },
            ],
          }
        : {}),
    };
  }
  return {
    name: '',
    ...(isFramework
      ? {
          hasSalaryInfo: false,
          currency: currencies?.[0],
        }
      : {}),
    ...(isLevel ? { attributes: [] } : {}),
    ...(isLevel ? { description: '' } : {}),
    ...(isLevel ? { users: [] } : {}),
    ...(isLevel
      ? {
          salaryRange: [
            {
              min_salary: null,
              max_salary: null,
            },
          ],
        }
      : {}),
  };
};

const LEVELING_INFO = {
  NAME: 'name',
  FRAMEWORK: 'framework',
  TRACK: 'track',
};

export const prepareInitialDataToMove = (
  framework,
  track,
  level,
  isLevel = false
) => {
  return {
    [LEVELING_INFO.NAME]: isLevel ? level.name : track.name,
    [LEVELING_INFO.FRAMEWORK]: framework?.id,
    ...(isLevel ? { [LEVELING_INFO.TRACK]: track?.id } : {}),
  };
};

export const getFieldsToMove = (subtitle = '', isLevel = false) => {
  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'titleMaxLength',
        },
      ],
    },
    {
      name: LEVELING_INFO.FRAMEWORK,
      type: FIELD_TYPES.SELECT,
      optionsKey: 'frameworks',
      parser: {
        value: 'id',
        label: 'name',
      },
      ...(isLevel
        ? {
            dependants: [
              {
                name: LEVELING_INFO.TRACK,
              },
            ],
          }
        : {}),
    },
    ...(isLevel
      ? [
          {
            name: LEVELING_INFO.TRACK,
            type: FIELD_TYPES.SELECT,
            optionsKey: 'tracks',
            parser: {
              value: 'id',
              label: 'name',
            },
            parent: {
              name: LEVELING_INFO.FRAMEWORK,
              options: 'frameworks',
            },
            validators: [{ validator: isEmpty, type: 'required' }],
          },
        ]
      : []),
  ];
};

export const getFields = (
  shouldValidate = true,
  framework = {},
  subtitle = ''
) => {
  const isFramework = !isEmpty(subtitle);

  return [
    ...(!isFramework
      ? [
          {
            name: 'pathSubtitle',
            type: FIELD_TYPES.SUBTITLE,
            label: subtitle,
            variant: 'body2',
          },
        ]
      : []),
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      shouldValidateInitialValue: shouldValidate,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'titleMaxLength',
        },
        ...(isFramework
          ? [
              {
                type: 'uniqueFrameworkName',
                validator: asyncDebounce(value => {
                  const params = {
                    name: trimString(value),
                    ...(!isObjectEmpty(framework)
                      ? { exclude_id: framework.id }
                      : {}),
                  };

                  return http
                    .get(API_UNIQUE_FRAMEWORK, { params })
                    .then(({ data }) => !data);
                }, 300),
                async: true,
              },
            ]
          : []),
      ],
    },
    ...(isFramework
      ? [
          {
            name: 'hasSalaryInfo',
            type: FIELD_TYPES.SWITCH,
            translationKey: 'salaryInfo',
            fieldWrapperClass: 'salaryInfo',
            isHighlightedLabel: true,
          },
          {
            name: 'currency',
            type: FIELD_TYPES.SELECT,
            optionsKey: 'currencies',
            noOptionsLabel: 'no options',
            shouldReturnOption: true,
            shouldRender: values => values.hasSalaryInfo,
            isToggleable: true,
            parser: {
              label: 'name',
              value: 'key',
            },
          },
        ]
      : []),
  ];
};

export const getTranslatedCurrencies = labels =>
  CURRENCIES.map(currency => ({
    ...currency,
    name: labels[currency.key],
  }));

export const getLevelFields = (
  isEdit,
  selectedCurrency,
  classes,
  subtitle = ''
) => {
  const hasSalaryInfo = isEmpty(selectedCurrency);

  let currency = '';
  if (hasSalaryInfo) {
    currency = isEdit
      ? CURRENCIES.find(curr => curr.key === selectedCurrency)?.value
      : selectedCurrency;
  }

  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'titleMaxLength',
        },
      ],
    },
    {
      name: 'description',
      type: FIELD_TYPES.RICH_TEXT_EDITOR,
      required: true,
      validators: [
        {
          type: 'levelMaxLength',
          validator: fieldValue => validateLength(fieldValue, 0, 5000),
        },
      ],
    },
    {
      name: 'attributes',
      type: FIELD_TYPES.SELECT_WITH_RANGE,
      optionsKey: 'attributesWithQuestions',
      isMulti: true,
      hasColorBox: true,
      shouldRemoveFieldSpacing: true,
      shouldRemoveFieldError: true,
      validators: [
        {
          type: 'attributeRange',
          validator: value => validateRange(value),
        },
      ],
      parser: {
        value: 'id',
        label: 'name',
      },
    },
    ...(hasSalaryInfo
      ? [
          {
            name: 'salarySubtitle',
            type: FIELD_TYPES.SUBTITLE,
            variant: 'subtitle1',
            fieldWrapperClass: classes.salarySubtitle,
          },
          {
            name: 'salaryRange',
            type: FIELD_TYPES.SALARY_RANGE,
            fieldTypes: SALARY_FIELDS,
            currency,
            validators: [
              {
                validator: salaryRanges =>
                  validateFieldsInList(salaryRanges, SALARY_FIELDS),
                isMultiple: true,
              },
            ],
          },
        ]
      : []),
  ];
};

export const getAssignedUsersToLevelFields = (subtitle = '') => {
  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'assignUsers',
      type: FIELD_TYPES.SELECT,
      optionsKey: 'allUsers',
      isUser: true,
      isMulti: true,
      shouldCalculateSelectionHeight: true,
      shouldRemoveFieldSpacing: true,
      parser: {
        value: 'id',
        label: 'name',
      },
    },
  ];
};

export const getInitialUsersData = assignedTo => ({
  assignUsers: getObjectToNumberArray(assignedTo),
});

export const getSalariesFields = (
  selectedTrack,
  selectedFramework,
  classes
) => {
  if (!selectedTrack.levels || isArrayEmpty(selectedTrack.levels)) return [];

  return [
    {
      name: 'trackName',
      type: FIELD_TYPES.SUBTITLE_WITH_TOOLTIP,
      label: selectedTrack.name,
      variant: 'h5',
      fieldWrapperClass: classes.groupSalarySubtitle,
    },
    {
      name: 'salaryRange',
      type: FIELD_TYPES.SALARY_RANGE,
      fieldTypes: SALARY_FIELDS,
      currency: selectedFramework.currency,
      validators: [
        {
          validator: salaryRanges =>
            validateFieldsInList(salaryRanges, SALARY_FIELDS),
          isMultiple: true,
        },
      ],
    },
  ];
};

export const getInitialSalariesData = selectedTrack => {
  if (!selectedTrack.levels || isArrayEmpty(selectedTrack.levels))
    return { salaryRange: [] };

  return {
    id: selectedTrack.id,
    salaryRange: selectedTrack.levels.map(level => ({
      id: level.id,
      name: level.name,
      min_salary: level.minSalary,
      max_salary: level.maxSalary,
    })),
  };
};
